import React, { useRef, useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Layer, Image, Line, Stage, Text as KonvaText, Group, Transformer, Tag, Rect } from 'react-konva';
import SketchLine from '../SketchLine';
import SketchLabel from '../SketchLabel';
import { SketchToolContext } from '../context';
import {
  getShapeLabel,
  getPoints,
  getLineLabels,
  calculateMapDimensions,
  scaleCoordinates,
  getCoordinateScale,
  calculateScale,
} from '../utils';
import CompassNorth from '../../../images/compass-north.png';
import { Button, Grid } from '@material-ui/core';
import { replaceLayers } from '../actions';
import SketchLabelGroup from '../SketchLabelGroup/SketchLabelGroup';

const LABEL_FONT_SIZE = 16;
const LABEL_FONT_SIZE_COEF = 1.3;

const DEFAULT_WIDTH = 800; // DO NOT CHANGE... scaling is based on these values
const DEFAULT_HEIGHT = 600; // DO NOT CHANGE... scaling is based on these values

const cloneJson = json => {
  return JSON.parse(JSON.stringify(json));
};

const defaultTransformShapeInfo = {
  id: null,
  layer: null,
  layerIndex: null,
  latLngIndex: null,
};

const SketchDrawing = ({
  layers = [],
  defaultWidth = DEFAULT_WIDTH,
  defaultHeight = DEFAULT_HEIGHT,
  onDrawComplete = () => null,
  enableEdit,
  shouldSave,
  setShouldSave,
  // layerData,
}) => {
  const container = useRef(null);
  const [width, setWidth] = useState(defaultWidth);
  const [height, setHeight] = useState(defaultHeight);
  const [compassImage, setCompassImage] = useState(null);
  const [selectedTransformShapeInfo, setSelectedTransformShapeInfo] = useState(defaultTransformShapeInfo);
  const [hasChanges, setHasChanges] = useState(false);
  const [changeHistory, setChangeHistory] = useState([]);
  const [shouldDeleteLabel, setShouldDeleteLabel] = useState(null);
  const [stageProps, setStageProps] = useState({
    scaleX: 0.85,
    scaleY: 0.85,
    x: 1,
    y: 1,
  });

  const [tempLayers, setTempLayers] = useState(cloneJson(layers));

  const mapDimensions = {
    ...calculateMapDimensions(layers),
    stageWidth: width,
    stageHeight: height,
  };

  const defaultScale = getCoordinateScale({
    ...mapDimensions,
    stageWidth: DEFAULT_WIDTH,
    stageHeight: DEFAULT_HEIGHT,
  });
  const normalizedScale = getCoordinateScale(mapDimensions);

  // NOT WORKING... somehow both values always come back the same
  const fontScaleCoef = LABEL_FONT_SIZE / defaultScale;
  const fontSize = fontScaleCoef * (normalizedScale * LABEL_FONT_SIZE_COEF);
  // console.log(defaultWidth, defaultHeight, defaultScale, normalizedScale, fontScaleCoef, fontSize);

  const {
    state: { layerData },
    dispatch,
  } = useContext(SketchToolContext);

  const handleWheel = e => {
    e.evt.preventDefault();

    const scaleBy = 1.1;
    const stage = e.target.getStage();
    const oldScale = stage.scaleX();
    const mousePointTo = {
      x: stage.getPointerPosition().x / oldScale - stage.x() / oldScale,
      y: stage.getPointerPosition().y / oldScale - stage.y() / oldScale,
    };

    const newScale = -e.evt.deltaY > 0 ? oldScale * scaleBy : oldScale / scaleBy;

    setStageProps({
      ...stageProps,
      scaleX: newScale,
      scaleY: newScale,
      x: -(mousePointTo.x - stage.getPointerPosition().x / newScale) * newScale,
      y: -(mousePointTo.y - stage.getPointerPosition().y / newScale) * newScale,
    });
  };

  const checkSize = () => {
    try {
      setWidth(container.current.attrs.container.offsetWidth);
      setHeight(container.current.attrs.container.offsetHeight);
    } catch (e) {
      console.log(e);
    }
  };

  const saveEditedLayers = () => {
    dispatch(replaceLayers(tempLayers));

    setHasChanges(false);
    setChangeHistory([]);
  };

  const undoChange = () => {
    if (!changeHistory.length) return;

    const lastChange = changeHistory.slice().pop();
    const tLayers = cloneJson(tempLayers);

    const { type, layerIndex, latLngIndex, oldValue } = lastChange;

    if (['layer', 'shapeLabel'].includes(type)) {
      // console.log(tLayers[layerIndex], oldValue);
      tLayers[layerIndex] = oldValue;
    } else {
      // console.log(tLayers[layerIndex][latLngIndex], oldValue);
      tLayers[layerIndex].latLngs[latLngIndex] = oldValue;
    }

    const newChangeHistory = changeHistory.slice(0, -1);
    // console.log(newChangeHistory);
    setChangeHistory(newChangeHistory);
    setTempLayers(null);
    setTempLayers(tLayers);
    !newChangeHistory.length && setHasChanges(false);
  };

  const updateTempLayer = layerId => evt => {
    const { x, y } = evt.target.attrs;

    const tLayers = cloneJson(tempLayers);

    const layerIndex = tempLayers.findIndex(layer => layer.id === layerId);

    // add the pre-change data to the history array
    setChangeHistory(
      changeHistory.concat({
        type: 'layer',
        layerIndex,
        latLngIndex: null,
        oldValue: cloneJson(tLayers[layerIndex]),
      }),
    );

    // make the updates
    tLayers[layerIndex].layerXOffset = x / normalizedScale;
    tLayers[layerIndex].layerYOffset = y / normalizedScale;

    // set the updates
    setTempLayers(tLayers);
    setHasChanges(true);
  };

  const updateLabel = (layerId, latLng, props) => {
    const tLayers = cloneJson(tempLayers);

    // find the layer and latLng indexes to edit
    const layerIndex = tempLayers.findIndex(layer => layer.id === layerId);
    const latLngIndex = tempLayers[layerIndex].latLngs.findIndex(
      ({ lat, lng }) => lat === latLng.lat && lng === latLng.lng,
    );

    // add the pre-change data to the history array
    setChangeHistory(
      changeHistory.concat({
        type: 'label',
        layerIndex,
        latLngIndex,
        oldValue: cloneJson(tLayers[layerIndex].latLngs[latLngIndex]),
      }),
    );

    // console.log(layerId, layerIndex, latLngIndex, cloneJson(tLayers[layerIndex].latLngs[latLngIndex]));

    // make the updates
    Object.keys(props).forEach(key => {
      tLayers[layerIndex].latLngs[latLngIndex][key] = props[key];
    });

    // set the updates
    setTempLayers(tLayers);
    setHasChanges(true);
  };

  const updateShapeLabel = (layerId, props) => {
    const tLayers = cloneJson(tempLayers);

    // find the layer index to edit
    const layerIndex = tempLayers.findIndex(layer => layer.id === layerId);

    // add the pre-change data to the history array
    setChangeHistory(
      changeHistory.concat({
        type: 'shapeLabel',
        layerIndex,
        latLngIndex: null,
        oldValue: cloneJson(tLayers[layerIndex]),
      }),
    );

    // make the updates
    Object.keys(props).forEach(key => {
      tLayers[layerIndex][key] = props[key];
    });

    // set the updates
    setTempLayers(tLayers);
    setHasChanges(true);
  };

  const updateLabelPosition = (layerId, latLng) => evt => {
    const { x, y } = evt.target.attrs;
    evt.cancelBubble = true;

    updateLabel(layerId, latLng, {
      labelXOffset: x / normalizedScale,
      labelYOffset: y / normalizedScale,
    });
  };

  const updateShapeLabelPosition = layerId => evt => {
    const { x, y } = evt.target.attrs;
    evt.cancelBubble = true;

    updateShapeLabel(layerId, {
      shapeLabelXOffset: x / normalizedScale,
      shapeLabelYOffset: y / normalizedScale,
    });
  };

  const onLabelTransformEnd = (layerId, latLng) => newAttrs => {
    const { rotation, scaleX, scaleY, x, y } = newAttrs;
    updateLabel(layerId, latLng, {
      rotation,
      scaleX,
      scaleY,
      //
      labelXOffset: x / normalizedScale,
      labelYOffset: y / normalizedScale,
    });
  };

  const onShapeLabelTransformEnd = layerId => newAttrs => {
    const { rotation, scaleX, scaleY, x, y } = newAttrs;

    // console.log(newAttrs);

    updateShapeLabel(layerId, {
      shapeLabelRotation: rotation,
      shapeLabelScaleX: scaleX,
      shapeLabelScaleY: scaleY,
      //
      shapeLabelXOffset: x / normalizedScale,
      shapeLabelYOffset: y / normalizedScale,
    });
  };

  // const removeLabel = (layerId, latLng) => evt =>
  //   console.log('fuck') &&
  //   ['Delete'].includes(evt.key) &&
  //   updateLabel(layerId, latLng, {
  //     hidden: true,
  //   });

  const getLabelId = (layer, index) => {
    const l = `${layer.id}__${index}`;
    return l;
  };

  const handleRemoveLabel = keyPressed => {
    console.log(selectedTransformShapeInfo);

    const { layer, layerIndex, latLngIndex, excludeFromDelete } = selectedTransformShapeInfo;

    if (!['Delete'].includes(keyPressed) || excludeFromDelete) return;

    updateLabel(layer.id, layer.latLngs[latLngIndex], {
      hidden: true,
    });
  };

  useEffect(() => {
    if (shouldDeleteLabel) {
      handleRemoveLabel(shouldDeleteLabel);
    }
    setShouldDeleteLabel(null);
  }, [shouldDeleteLabel]);

  useEffect(() => {
    window.addEventListener('resize', checkSize);
    window.addEventListener('keydown', evt => setShouldDeleteLabel(evt.key));

    checkSize();
    onDrawComplete(container.current.getStage().toDataURL());

    // create the compass image
    const compass = new window.Image();
    compass.src = CompassNorth;
    compass.width = 75;
    compass.height = 75;
    setCompassImage(compass);

    return () => {
      window.removeEventListener('resize', checkSize);
    };
  }, []);

  useEffect(() => {
    if (shouldSave) {
      saveEditedLayers();
      setShouldSave(false);
    }
  }, [shouldSave]);

  // console.log('layers', layers);
  // console.log(width, height, xScale, yScale);

  return (
    <React.Fragment>
      <Stage
        align="center"
        ref={container}
        width={width}
        height={height}
        draggable
        onWheel={handleWheel}
        onMouseDown={e => {
          // deselect when clicked on empty area
          const clickedOnEmpty = e.target === e.target.getStage();
          if (clickedOnEmpty) {
            setSelectedTransformShapeInfo(defaultTransformShapeInfo);
          }
        }}
        {...stageProps}
      >
        {tempLayers
          .filter(layer => layer.enabled)
          .map((layer, layerIndex) => {
            const shapeLabel = getShapeLabel(
              { ...mapDimensions, stageWidth: width, stageHeight: height },
              layer.latLngs,
              layer,
              layerData,
            );
            const points = getPoints(
              { ...mapDimensions, stageWidth: width, stageHeight: height },
              layer.latLngs,
              layer,
              layerData,
            );

            return (
              <Layer
                align="center"
                style={{ backgroundColor: 'white' }}
                key={`Layer${layer.id}`}
                draggable={enableEdit}
                onDragEnd={updateTempLayer(layer.id)}
                x={(layer.layerXOffset || 0) * normalizedScale}
                y={(layer.layerYOffset || 0) * normalizedScale}
              >
                <SketchLine id={layer.id} points={points} />
                {getLineLabels(
                  { ...mapDimensions, stageWidth: width, stageHeight: height },
                  Array.from(layer.latLngs),
                  normalizedScale,
                )
                  .filter(label => !label.hidden)
                  .map(label => (
                    <SketchLabel
                      fill={'#777'}
                      isSelected={!!label.text && selectedTransformShapeInfo.id === getLabelId(layer, label.index)}
                      onSelect={() => {
                        enableEdit &&
                          setSelectedTransformShapeInfo({
                            id: getLabelId(layer, label.index),
                            layer,
                            layerIndex,
                            latLngIndex: layer.latLngs.findIndex(
                              ({ lat, lng }) => lat === label.lat && lng === label.lng, // TODO.babyjay: could we just use label index??? not sure if they get mapped exactly to the same order in the utils
                            ),
                          });
                        // && setSelectedTransformShapeId()
                      }}
                      onTransformEnd={
                        enableEdit &&
                        onLabelTransformEnd(layer.id, {
                          lat: label.lat,
                          lng: label.lng,
                        })
                      }
                      shapeProps={{
                        // fontSize,
                        x: label.x || 0,
                        y: label.y || 0,
                        rotation: label.rotation,
                        scaleX: label.scaleX,
                        scaleY: label.scaleY,

                        text: label.text,
                        draggable: enableEdit,
                        onDragEnd:
                          enableEdit &&
                          updateLabelPosition(layer.id, {
                            lat: label.lat,
                            lng: label.lng,
                          }),
                      }}
                      // setupDeleteKeydownEvent={setupDeleteKeydownEvent}
                      // removeDeleteKeydownEvent={removeDeleteKeydownEvent}
                      // removeLabel={removeLabel(layer.id, {
                      //   lat: label.lat,
                      //   lng: label.lng,
                      // })}
                    />
                  ))}
                <SketchLabelGroup
                  shapeProps={{
                    draggable: enableEdit,
                    onDragEnd: updateShapeLabelPosition(layer.id),
                    x: (layer.shapeLabelXOffset || 0) * normalizedScale,
                    y: (layer.shapeLabelYOffset || 0) * normalizedScale,
                    scaleX: layer.shapeLabelScaleX,
                    scaleY: layer.shapeLabelScaleY,
                    rotation: layer.shapeLabelRotation,
                  }}
                  onTransformEnd={enableEdit && onShapeLabelTransformEnd(layer.id)}
                  onSelect={() =>
                    enableEdit &&
                    setSelectedTransformShapeInfo({
                      id: getLabelId(layer, 'group_label'),
                      layer,
                      layerIndex,
                      excludeFromDelete: true,
                    })
                  }
                  isSelected={selectedTransformShapeInfo.id === getLabelId(layer, 'group_label')}
                  _useStrictMode
                >
                  <SketchLabel
                    shapeProps={{
                      // fontSize,
                      fontStyle: 'bold',
                      x: shapeLabel.titleX,
                      y: shapeLabel.titleY,
                      text: shapeLabel.title,
                    }}
                  />
                  <SketchLabel
                    shapeProps={{
                      // fontSize,
                      fontStyle: 'bold',
                      x: shapeLabel.subtitleX,
                      y: shapeLabel.subtitleY,
                      text: shapeLabel.subtitle,
                    }}
                  />
                </SketchLabelGroup>
              </Layer>
            );
          })}
      </Stage>
      <Grid container justify="space-between" align="center">
        <div style={{ color: 'red' }}>{hasChanges ? '* You have unsaved changes. Save before continuing. ' : ''}</div>
        {enableEdit && (
          <Button onClick={undoChange} disabled={!changeHistory.length}>
            Undo Last Change
          </Button>
        )}
      </Grid>
    </React.Fragment>
  );
};
SketchDrawing.propTypes = {
  layers: PropTypes.array,
  enableEdit: PropTypes.bool,
  shouldSave: PropTypes.bool,
  setShouldSave: PropTypes.func,
};

SketchDrawing.defaultProps = {
  enableEdit: false,
};
export default SketchDrawing;
